import { Component, EventEmitter, Input, Output, ViewEncapsulation, OnChanges, SimpleChanges } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { ConfidenceModel } from '../../shared/project/cards/confidence-model';
import { AltCardView, Card } from '../../shared/project/cards/card';
import { WaterfallItem2 } from '../waterfall-item';
import { CardHelper, ChartOptions } from '../cardHelper';
import { FancyNumberPipe } from '../../shared/directives/fancy-number.pipe';
import { ProjectService } from '../../shared/project/project.service';
import { DriverSelectionAlgorithm } from '../../shared/project/cards/driver-selection-algorithm';
import { DriverType } from '../../shared/project/driver-types/driver-type';
import { RelatedCardsSettingsModalComponent, RelatedCardsSettingsModalData } from './related-cards-settings-modal.component';

@Component({
    selector: 'app-related-cards',
    templateUrl: './related-cards.component.html',
    styleUrls: ['./related-cards.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class RelatedCardsComponent implements OnChanges {
    DriverType: typeof DriverType = DriverType;   //hack for enum to be available in the template
    @Input() confidenceModel: ConfidenceModel;
    // The page's SEQ/TOT switch: /cards has a single one, which its Walk, Summary, Trend and Related all show and set
    @Input() seqMode: boolean = true;
    @Output() seqModeChange = new EventEmitter<boolean>();
    @Output() reloadCards = new EventEmitter<void>();
    loading: boolean = true;
    loadingMore: boolean = false;
    private cardsCount: number = 4;
    selectedAlgorithm: DriverSelectionAlgorithm = DriverSelectionAlgorithm.Smart;
    showLoadMoreCardBtn = true;

    public viewBoxWidth = 100;
    public viewBoxHeight = 40;

    cardOutcomeCtx = FancyNumberPipe.createDefaultContext();
    cardPercCtx = {
        roundTo: 1,
        scale: undefined,
        showPlusForPositive: true,
        use2stepRounding: false,
    };

    public chartOptions: ChartOptions = CardHelper.createChartOptions();

    hasFormCompFromTo(altView: AltCardView): boolean {
        return CardHelper.hasFormCompFromTo(altView);
    }

    constructor(private projectService: ProjectService,private dialog: MatDialog) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['confidenceModel']) {
            if (this.confidenceModel) {
                this.loading = false;
                this.loadModel();
            } else {
                this.loading = true;
            }
        } else if (changes['seqMode']) {
            this.loadWaterfallItems();
        }
    }

    private loadModel(newModel: ConfidenceModel | undefined = undefined): void {
        if (newModel) {
            this.confidenceModel = newModel;
        }
        if (this.cardsCount > this.confidenceModel.cards.length) {
            this.showLoadMoreCardBtn = false;
        }
        this.loadWaterfallItems();
        this.initCardDiagrams();
        this.calcFancyNumContexts();
    }

    setSeqMode(val: boolean): void {
        this.seqMode = val;
        this.loadWaterfallItems();
        this.seqModeChange.emit(val);
    }

    private loadWaterfallItems(): void {
        if (!this.confidenceModel) {
            return;
        }

        const MAX_HEIGHT = 125;             // px

        // Find max values across all cards (independent, not cumulative)
        let seqDiff = this.confidenceModel.selectedCard.seqDriverValue;
        let absDiff = this.confidenceModel.selectedCard.absDriverValue;
        let seqMax = Math.abs(seqDiff);
        let absMax = Math.abs(absDiff);

        for (let i = 0; i < this.confidenceModel.cards.length; i++) {
            const card = this.confidenceModel.cards[i];
            seqDiff = card.seqDriverValue;
            absDiff = card.absDriverValue;
            console.log(`Card ${i}: seqDriverValue = ${card.seqDriverValue}, absDriverValue = ${card.absDriverValue}`);
            if (seqMax < Math.abs(seqDiff)) {
                seqMax = Math.abs(seqDiff);
            }
            if (absMax < Math.abs(absDiff)) {
                absMax = Math.abs(absDiff);
            }
        }

        this.createWaterfallItem(this.confidenceModel.selectedCard, seqMax, absMax, MAX_HEIGHT);
        for (let card of this.confidenceModel.cards) {
            this.createWaterfallItem(card, seqMax, absMax, MAX_HEIGHT);
        }
    }

    private createWaterfallItem(card: Card, seqMax: number, absMax: number, totalHeight: number): void {
        const POS_COLOUR = '#5C5C5C';
        const NEG_COLOUR = '#DE3F3F';
        let item = new WaterfallItem2();
        let seqDiff = card.seqDriverValue;
        let absDiff = card.absDriverValue;
        const maxValue = this.seqMode ? seqMax : absMax;
        item.seqHeight = Math.abs(seqDiff / maxValue * totalHeight);
        item.absHeight = Math.abs(absDiff / maxValue * totalHeight);
        item.seqBottom = 0;
        item.absBottom = 0;

        item.seqColor = card.seqDriverValue >= 0 ? POS_COLOUR : NEG_COLOUR;
        item.absColor = card.absDriverValue >= 0 ? POS_COLOUR : NEG_COLOUR;

        card.waterfallItem = item;
    }

    private initCardDiagrams(): void {
        if (!this.confidenceModel) {
            return;
        }

        CardHelper.initCardDiagram(
            this.confidenceModel.selectedCard,
            this.confidenceModel.selectedDiagramSeriesName,
            this.confidenceModel.oldControlValue,
            this.confidenceModel.newControlValue,
            true,
            this.viewBoxWidth,
            this.viewBoxHeight
        );

        for (let card of this.confidenceModel.cards) {
            if (card.altViewSeq && card.altViewSeq.diagramSeries && card.altViewSeq.diagramSeries.length) {
                CardHelper.initCardDiagram(
                    card,
                    this.confidenceModel.selectedDiagramSeriesName,
                    this.confidenceModel.oldControlValue,
                    this.confidenceModel.newControlValue,
                    true,
                    this.viewBoxWidth,
                    this.viewBoxHeight
                );
            }
        }
    }

    private calcFancyNumContexts(): void {
        if (!this.confidenceModel) {
            return;
        }

        CardHelper.calcCardsFancyNumContexts([this.confidenceModel.selectedCard]);
        CardHelper.calcCardsFancyNumContexts(this.confidenceModel.cards);
        this.cardOutcomeCtx = CardHelper.calcOutcomeFancyNumContext(
            this.confidenceModel.cards,
            [
                this.confidenceModel.outcomeBeforeCard,
                this.confidenceModel.selectedCard.seqDriverValue,
                this.confidenceModel.selectedCard.absDriverValue
            ]
        );
        this.cardOutcomeCtx.showPlusForPositive = true;
    }

    public cardMouseEnter(card: Card): void {
        card.cardHovered = true;
    }

    public cardMouseLeave(card: Card): void {
        card.cardHovered = false;
    }

    public async removeCard(i: number) {
        this.confidenceModel.cards.splice(i, 1);
    }

    dragStarted(event: CdkDragDrop<Card[]>) {
    }

    async dropped(event: CdkDragDrop<Card[]>) {
        if (event.previousIndex == event.currentIndex) {
            return;
        }
        let dragged = this.confidenceModel.cards[event.previousIndex];
        this.confidenceModel.cards.splice(event.previousIndex, 1);
        this.confidenceModel.cards.splice(event.currentIndex, 0, dragged);
        this.loadWaterfallItems();
    }

    public triggerReload(): void {
        this.reloadCards.emit();
    }

    public async replaceCard (i: number) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            await this.projectService.replaceCard(this.confidenceModel.viewId, this.confidenceModel.tabId, this.confidenceModel.cardIndex,
                this.confidenceModel.cards[i].allDimensions, this.confidenceModel.cards[i].driverType, this.confidenceModel.cards[i].formulaComponentIndex);

            this.triggerReload();
        } finally {
            this.loading = false;
        }
    }

    public async addToParent(i: number) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            await this.projectService.addCardBeforeConfidenceCheckedCard(this.confidenceModel.viewId, this.confidenceModel.tabId, this.confidenceModel.cardIndex,
                this.confidenceModel.cards[i].allDimensions, this.confidenceModel.cards[i].driverType, this.confidenceModel.cards[i].formulaComponentIndex);

            this.triggerReload();
        } finally {
            this.loading = false;
        }
    }

    public async loadMoreCards() {
        try {
            this.loadingMore = true;
            this.cardsCount += 4;
            let confidenceModel = await this.projectService.getConfidence(this.confidenceModel.viewId, this.confidenceModel.tabId, this.confidenceModel.cardIndex, this.cardsCount, this.selectedAlgorithm);
            this.loadModel(confidenceModel);
        } finally {
            this.loadingMore = false;
        }
    }

    public openSettingsModal(): void {
        const dialogData: RelatedCardsSettingsModalData = {
            selectedAlgorithm: this.selectedAlgorithm,
            outcomeName: this.confidenceModel?.outcomeName
        };

        const dialogRef = this.dialog.open(RelatedCardsSettingsModalComponent, {
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async (result: DriverSelectionAlgorithm) => {
            if (result !== undefined && result !== this.selectedAlgorithm) {
                this.selectedAlgorithm = result;
                await this.reloadCardsWithNewAlgorithm();
            }
        });
    }

    private async reloadCardsWithNewAlgorithm(): Promise<void> {
        if (!this.confidenceModel) {
            return;
        }
        try {
            this.loading = true;
            let confidenceModel = await this.projectService.getConfidence(
                this.confidenceModel.viewId,
                this.confidenceModel.tabId,
                this.confidenceModel.cardIndex,
                this.cardsCount,
                this.selectedAlgorithm
            );
            this.loadModel(confidenceModel);
        } finally {
            this.loading = false;
        }
    }

}
